<template>
<div>
	<b-navbar class="submenu">
		<b-container>
			<b-navbar-brand href="#">Policy Sections Admin</b-navbar-brand>
			<b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
			<b-collapse id="nav-collapse" is-nav>
				<b-navbar-nav>
					<a href="#" @click="saveContent()" v-if="selectedFile">Save</a>
				</b-navbar-nav>
			</b-collapse>
		</b-container>
	</b-navbar>

	<b-alert
		v-model="showAlert"
		:variant="alertVariant"
		dismissible
		class="alert-fixed"
	>
		{{ alertMessage }}
	</b-alert>

	<div class="d-flex">
		<div class="file-panel">
			<div v-for="f in filtered" 
				:key="f.fileName" 
				class="file-item"
				:class="{ 'selected': selectedFile === f.fileName }"
				@click="selectFile(f)">
				{{f.fileName}}
			</div>
		</div>
		<div class="content-panel">
			<div v-if="selectedFile" class="content-wrapper">
				<div v-if="isLoading" class="text-center pa-4">
					<b-spinner variant="primary" type="grow"></b-spinner>
					<div class="mt-2">Loading file content...</div>
				</div>
				<div v-else-if="loadError" class="error-message pa-4">
					{{ loadError }}
				</div>
				<div v-else class="editor-container" ref="editorContainer"></div>
			</div>
			<div v-else>
				<h4>Select a file to edit</h4>
			</div>
		</div>
	</div>

	<b-modal
		v-model="showUnsavedWarning"
		title="Unsaved Changes"
		@ok="confirmFileSwitch"
		@cancel="cancelFileSwitch"
		ok-variant="danger"
		ok-title="Discard Changes"
		cancel-title="Stay on Current File"
	>
		<p>You have unsaved changes. Are you sure you want to switch files?</p>
		<p class="text-muted">Your changes will be lost if you proceed.</p>
	</b-modal>
</div>
</template>

<script>
import loader from '@monaco-editor/loader'

export default {
  name: 'OfflinePolicySections',
  data() {
    return {
      image: '',
      fileName: '',
      lastModifiedDateTime: '',
      uri: '',
      files: null,
      selectedFile: null,
      fileContent: '',
      isLoading: false,
      loadError: null,
      showAlert: false,
      alertVariant: 'success',
      alertMessage: '',
      editor: null,
      monaco: null,
      editorInitialized: false,
      showUnsavedWarning: false,
      pendingFileSwitch: null,
      hasUnsavedChanges: false,
      originalContent: ''
    }
  },
  computed: {
    filtered() {
      if (!this.files) { return null; }
      return this.files.filter(f => { return f.fileName.toLowerCase().includes(".json")});
    }
  },
  methods: {
    async initMonaco() {
      if (this.monaco) return;

      try {
        // Configure loader
        loader.config({
          paths: {
            vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.36.1/min/vs'
          }
        });

        this.monaco = await loader.init();
        console.log('Monaco initialized');
        this.editorInitialized = true;
      } catch (error) {
        console.error('Error initializing Monaco:', error);
      }
    },
    async initEditor() {
      if (!this.editorInitialized) {
        await this.initMonaco();
      }

      if (this.editor) {
        this.editor.dispose();
      }

      const container = this.$refs.editorContainer;
      if (!container) {
        console.error('Editor container not found');
        return;
      }

      try {
        // Create editor instance
        this.editor = this.monaco.editor.create(container, {
			value: this.fileContent,
			language: 'json',
			theme: 'vs-dark',
			automaticLayout: true,
			minimap: { enabled: true },
			scrollBeyondLastLine: false,
			fontSize: 14,
			tabSize: 2,
			wordWrap: 'on',
			formatOnPaste: true,
			formatOnType: true,
			validateOnType: true,
			validateOnPaste: true,
			validationDelay: 0,
			lineNumbers: 'on',
			glyphMargin: true,
			lineDecorationsWidth: 5,
			lineNumbersMinChars: 3,
			overviewRulerBorder: true,
			overviewRulerLanes: 2,
			hideCursorInOverviewRuler: false,
			renderValidationDecorations: 'editable',
			renderLineHighlight: 'all',
			renderIndentGuides: true,
			renderWhitespace: 'all',
			folding: true,
			foldingStrategy: 'indentation',
			showFoldingControls: 'always'
        });

        // Configure JSON language features
        this.monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
          validate: true,
          allowComments: false,
          trailingCommas: 'error',
          enableSchemaRequest: true,
          schemas: [{
            uri: "http://json-schema.org/draft-07/schema",
            fileMatch: ["*"],
            schema: {
              type: "object",
              additionalProperties: true
            }
          }]
        });

        // Handle content changes
        this.editor.onDidChangeModelContent(() => {
          const currentContent = this.editor.getValue();
          this.fileContent = currentContent;
          this.hasUnsavedChanges = currentContent !== this.originalContent;
          
          const model = this.editor.getModel();
          if (model) {
            try {
              JSON.parse(model.getValue());
              this.monaco.editor.setModelMarkers(model, 'json', []);
            } catch (e) {
              // Create a marker that spans the entire line
              const lineNumber = model.getLineCount();
              this.monaco.editor.setModelMarkers(model, 'json', [{
                startLineNumber: lineNumber,
                startColumn: 1,
                endLineNumber: lineNumber,
                endColumn: 1,
                message: e.message,
                severity: this.monaco.MarkerSeverity.Error,
                source: 'json'
              }]);
            }
          }
        });

        // Force validation
        this.editor.getAction('editor.action.formatDocument').run();
        
        console.log('Editor initialized successfully');
      } catch (error) {
        console.error('Error creating editor:', error);
      }
    },
    async selectFile(file) {
      if (this.hasUnsavedChanges) {
        this.pendingFileSwitch = file;
        this.showUnsavedWarning = true;
        return;
      }
      await this.loadFile(file);
    },
    async loadFile(file) {
      console.log('Loading file:', file);
      this.selectedFile = file.fileName;
      this.isLoading = true;
      this.loadError = null;
      this.fileContent = '';
      this.hasUnsavedChanges = false;
      
      try {
        const response = await this.$http.get(`${this.$config.ApiUri}/CMS/Configs/${file.fileName}`);
        console.log('File content response:', response.data);
        if (response.data) {
          const content = response.data.content ? atob(response.data.content) : JSON.stringify(response.data, null, 2);
          this.fileContent = content;
          this.originalContent = content;
          
          await this.$nextTick();
          setTimeout(async () => {
            await this.initEditor();
          }, 100);
        } else {
          this.loadError = 'No content found in file';
        }
      } catch (error) {
        console.error('Error loading file content:', error);
        this.loadError = 'Error loading file content: ' + (error.message || 'Unknown error');
      } finally {
        this.isLoading = false;
      }
    },
    async saveContent() {
      if (!this.selectedFile || !this.editor) return;
      
      try {
        const content = this.editor.getValue();
        JSON.parse(content);
        const base64Content = btoa(content);
        await this.$http.put(this.$config.ApiUri + "/CMS/Configs/" + this.selectedFile, 
          `"${base64Content}"`,
          {
            headers: {
              'Content-Type': 'application/json'
            },
            transformRequest: [(data) => data]
          }
        );
        this.originalContent = content;
        this.hasUnsavedChanges = false;
        this.showMessage('File saved successfully');
      } catch (error) {
        if (error instanceof SyntaxError) {
          this.showMessage('Invalid JSON format. Please fix the errors before saving.', 'danger');
        } else {
          console.error('Error saving file:', error);
          this.showMessage('Error saving file', 'danger');
        }
      }
    },
    showMessage(message, variant = 'success') {
      this.alertMessage = message;
      this.alertVariant = variant;
      this.showAlert = true;
      if (variant === 'success') {
        setTimeout(() => {
          this.showAlert = false;
        }, 5000);
      }
    },
    async listFiles() {
      console.log(this.$config.ApiUri + "/CMS/Configs");
      this.$http.get(this.$config.ApiUri + "/CMS/Configs")
      .then((result) => {
        console.log('Files from CMS/Configs:', result.data);
        this.files = result.data;
      })
    },
    confirmFileSwitch() {
      this.showUnsavedWarning = false;
      if (this.pendingFileSwitch) {
        this.loadFile(this.pendingFileSwitch);
        this.pendingFileSwitch = null;
      }
    },
    cancelFileSwitch() {
      this.showUnsavedWarning = false;
      this.pendingFileSwitch = null;
    }
  },
  async mounted() {
    await this.initMonaco();
    this.listFiles();
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.dispose();
    }
  }
}
</script>

<style scoped>
.file-panel {
  width: 300px;
  border-right: 1px solid #eee;
  height: calc(100vh - 56px);
  overflow-y: auto;
}

.content-panel {
  flex: 1;
  padding: 20px;
  height: calc(100vh - 56px);
  display: flex;
  flex-direction: column;
}

.content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content-wrapper h4 {
  margin-bottom: 16px;
}

.file-item {
  padding: 8px 16px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-item:last-child {
  border-bottom: none;
}

.file-item:hover {
  background-color: #f5f5f5;
}

.file-item.selected {
  background-color: #e3f2fd;
  border-left: 4px solid #1976d2;
}

.error-message {
  color: #d32f2f;
  background-color: #ffebee;
  border-radius: 4px;
}

.editor-container {
  height: calc(100vh - 120px);
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  background-color: #1e1e1e;
}

.alert-fixed {
  position: fixed;
  top: 56px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  min-width: 300px;
  text-align: center;
}
</style>